import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import CookieBanner from "@/components/CookieBanner";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Admin from "@/pages/Admin";
import Guide from "@/pages/Guide";
import ComplianceIndex from "@/pages/ComplianceIndex";
import SalePack from "@/pages/SalePack";
import SaleAccept from "@/pages/SaleAccept";
import Portfolio from "@/pages/Portfolio";
import Jobs from "@/pages/Jobs";
import ContractorJoin from "@/pages/ContractorJoin";
import Settings from "@/pages/Settings";
import ShareView from "@/pages/ShareView";
import Legal from "@/pages/Legal";
import EvidencePack from "@/pages/EvidencePack";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentSuccess";
import Tools from "@/pages/Tools";
import Shared from "@/pages/Shared";
import Verify from "@/pages/Verify";
import TenantPortal from "@/pages/TenantPortal";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_complete && !adminOnly && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/legal/:kind" element={<Legal />} />
      <Route path="/guides/:nation" element={<Guide />} />
      <Route path="/compliance-index" element={<ComplianceIndex />} />
      <Route path="/sale/accept/:token" element={<SaleAccept />} />
      <Route path="/sale/:token" element={<SalePack />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/properties" element={<Protected><Properties /></Protected>} />
      <Route path="/properties/:id" element={<Protected><PropertyDetail /></Protected>} />
      <Route path="/properties/:id/evidence" element={<Protected><EvidencePack /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/tools" element={<Protected><Tools /></Protected>} />
      <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
      <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
      <Route path="/contractor/join/:token" element={<ContractorJoin />} />
      <Route path="/shared" element={<Protected><Shared /></Protected>} />
      <Route path="/verify/:token" element={<Verify />} />
      <Route path="/tenant/:token" element={<TenantPortal />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
